/* =============================================================
 * <mega-drawer> — mobile megamenu drawer custom element.
 *
 * Drives the drill-down navigation rendered by mobile-mega-drawer.liquid.
 * Levels are pre-rendered in the DOM; this element just toggles which
 * one is .is-active and maintains a back-stack.
 *
 * Markup contract (all data attributes):
 *   <mega-drawer hidden>
 *     <button data-mega-drawer-close>   close the drawer
 *     <div data-mega-drawer-level="L0" class="is-active">  the root
 *     <div data-mega-drawer-level="L1-foo">                 sibling level
 *     <button data-mega-drawer-go-to="L1-foo">              navigate forward
 *     <button data-mega-drawer-back>                        pop the stack
 *     <ul data-mega-drawer-tiles class="is-collapsed">      collapsible tile list
 *     <button data-mega-drawer-expand                        toggle the above open/closed
 *             data-expand-label data-collapse-label>         (label swaps on toggle)
 *
 * Opening the drawer is triggered externally by setting attribute `open`
 * on the element (or by calling `.open()`). Compatible with existing
 * header trigger buttons via [aria-controls="MegaDrawer"].
 * ============================================================= */

class MegaDrawer extends HTMLElement {
  // Fallback collapsed tile-list height, used only when the CSS cap can't be
  // read (see collapsedCap). Grids cap at different heights —
  // `.mega-drawer__tiles`, `--image-full`, and `.mega-drawer__image-cards`
  // each set their own `.is-collapsed { max-height }` in megamenu-mobile.css.
  static COLLAPSED_MAX = 332;
  // Tile reveal/collapse duration — keep in sync with the max-height
  // transition on `.mega-drawer__tiles` in megamenu-mobile.css.
  static TILES_DURATION = 340;

  constructor() {
    super();
    this.stack = [];
    this.activeLevelId = null;
    this.initialLevelId = 'L0';
    this.tilesAnimating = false;
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);

    // Wire up external triggers (hamburger button etc.) via aria-controls.
    const id = this.getAttribute('id');
    if (id) {
      document.querySelectorAll(`[aria-controls="${id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });
    }
  }

  // The levels ship inside an inert <template> (see mobile-mega-drawer.liquid)
  // so their ~180KB of markup and per-tile images cost nothing at page load.
  // Stamping is synchronous, so the first open() can hydrate inline.
  hydrate() {
    if (this.levels) return;

    const template = this.querySelector('template[data-mega-drawer-template]');
    if (template) template.replaceWith(template.content);

    this.levels = Array.from(this.querySelectorAll('[data-mega-drawer-level]'));

    // Single shared bottom block (links + country selector + login) —
    // rendered once in L0 and moved into the active level on navigation.
    this.sharedBottom = this.querySelector('.mega-drawer__shared-bottom');

    // The root level is whichever already has .is-active on first render —
    // fall back to the first level if none are marked.
    const initial = this.querySelector('.mega-drawer__level.is-active') || this.levels[0];
    if (initial) {
      this.initialLevelId = initial.getAttribute('data-mega-drawer-level');
      this.activeLevelId = this.initialLevelId;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  open() {
    this.hydrate();
    if (!this.levels.length) return;
    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    // Reset to root each time the drawer opens.
    this.resetToRoot();
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.focusFirstFocusable(this.getActiveLevel());
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    // Match the CSS transition before fully hiding.
    setTimeout(() => {
      this.setAttribute('hidden', '');
    }, 240);
  }

  onClick(event) {
    const closeBtn = event.target.closest('[data-mega-drawer-close]');
    if (closeBtn) {
      event.preventDefault();
      this.close();
      return;
    }

    const backBtn = event.target.closest('[data-mega-drawer-back]');
    if (backBtn) {
      event.preventDefault();
      this.goBack();
      return;
    }

    const goToBtn = event.target.closest('[data-mega-drawer-go-to]');
    if (goToBtn) {
      // Split rows put the drill attribute on the row wrapper so the empty
      // space drills in — but a click landing on a real link inside that row
      // must still navigate.
      const link = event.target.closest('a[href]');
      if (link && goToBtn.contains(link)) return;

      event.preventDefault();
      const target = goToBtn.getAttribute('data-mega-drawer-go-to');
      if (target) this.goTo(target);
      return;
    }

    const expandBtn = event.target.closest('[data-mega-drawer-expand]');
    if (expandBtn) {
      event.preventDefault();
      this.toggleTiles(expandBtn);
      return;
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.hasAttribute('hidden')) return;
    event.preventDefault();
    if (this.stack.length > 0) {
      this.goBack();
    } else {
      this.close();
    }
  }

  goTo(levelId) {
    const target = this.querySelector(`[data-mega-drawer-level="${CSS.escape(levelId)}"]`);
    if (!target) return;
    const current = this.getActiveLevel();
    if (current) {
      current.classList.remove('is-active');
      current.setAttribute('hidden', '');
      this.stack.push(this.activeLevelId);
    }
    target.removeAttribute('hidden');
    target.classList.add('is-active');
    this.activeLevelId = levelId;
    this.moveSharedBottom(target);
    this.scrollTopOfActive();
    this.focusFirstFocusable(target);
  }

  moveSharedBottom(level) {
    if (!this.sharedBottom || !level) return;
    if (this.sharedBottom.parentElement !== level) level.appendChild(this.sharedBottom);
  }

  goBack() {
    const previous = this.stack.pop();
    if (!previous) return;
    const current = this.getActiveLevel();
    if (current) {
      current.classList.remove('is-active');
      current.setAttribute('hidden', '');
    }
    const target = this.querySelector(`[data-mega-drawer-level="${CSS.escape(previous)}"]`);
    if (target) {
      target.removeAttribute('hidden');
      target.classList.add('is-active');
      this.activeLevelId = previous;
      this.moveSharedBottom(target);
      this.scrollTopOfActive();
      this.focusFirstFocusable(target);
    }
  }

  resetToRoot() {
    this.stack = [];
    this.levels.forEach((lvl) => {
      lvl.classList.remove('is-active');
      lvl.setAttribute('hidden', '');
      // Reset any expanded tile lists back to their collapsed default,
      // instantly (no animation) since the drawer isn't visible yet.
      lvl.querySelectorAll('[data-mega-drawer-tiles]').forEach((tilesEl) => {
        const expandBtn = tilesEl.parentElement && tilesEl.parentElement.querySelector('[data-mega-drawer-expand]');
        if (!expandBtn) return; // short tile lists have no expand/collapse control
        tilesEl.classList.add('is-collapsed');
        tilesEl.style.maxHeight = '';
        tilesEl.style.overflow = '';
        this.setExpandBtnState(expandBtn, false, false);
      });
    });
    const root = this.querySelector(`[data-mega-drawer-level="${CSS.escape(this.initialLevelId)}"]`);
    if (root) {
      root.removeAttribute('hidden');
      root.classList.add('is-active');
      this.activeLevelId = this.initialLevelId;
      this.moveSharedBottom(root);
    }
  }

  toggleTiles(expandBtn) {
    const level = expandBtn.closest('[data-mega-drawer-level]');
    if (!level) return;
    const tilesEl = level.querySelector('[data-mega-drawer-tiles]');
    if (!tilesEl) return;
    if (this.tilesAnimating) return; // ignore rapid re-clicks mid-animation

    const expanded = expandBtn.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      this.collapseTiles(tilesEl, expandBtn);
    } else {
      this.expandTiles(tilesEl, expandBtn);
    }
  }

  expandTiles(tilesEl, expandBtn) {
    const full = tilesEl.scrollHeight;
    // Read the cap while .is-collapsed still applies, so the animation starts
    // from this grid's actual collapsed height.
    const cap = this.collapsedCap(tilesEl);
    // Dropping .is-collapsed removes the CSS max-height cap + fade mask.
    tilesEl.classList.remove('is-collapsed');

    if (this.prefersReducedMotion()) {
      tilesEl.style.maxHeight = '';
      tilesEl.style.overflow = '';
      this.setExpandBtnState(expandBtn, true);
      return;
    }

    // Re-pin to the collapsed height inline so there's no jump, then grow
    // to the measured full height on the next frame.
    tilesEl.style.overflow = 'hidden';
    tilesEl.style.maxHeight = `${cap}px`;
    void tilesEl.offsetHeight; // force reflow to lock in the start value
    this.animateTilesTo(tilesEl, `${full}px`, () => {
      // Let the grid flow naturally once open (content can reflow freely).
      tilesEl.style.maxHeight = '';
      tilesEl.style.overflow = '';
    });
    this.setExpandBtnState(expandBtn, true);
  }

  collapseTiles(tilesEl, expandBtn) {
    if (this.prefersReducedMotion()) {
      tilesEl.classList.add('is-collapsed');
      tilesEl.style.maxHeight = '';
      tilesEl.style.overflow = '';
      this.setExpandBtnState(expandBtn, false);
      return;
    }

    // Read the cap before pinning an inline max-height, otherwise the inline
    // value is what gets measured.
    const cap = this.collapsedCap(tilesEl);

    // Start from the current full height, then shrink back to the cap.
    tilesEl.style.overflow = 'hidden';
    tilesEl.style.maxHeight = `${tilesEl.scrollHeight}px`;
    void tilesEl.offsetHeight; // force reflow
    this.animateTilesTo(tilesEl, `${cap}px`, () => {
      // Restore the class (fade mask + CSS cap) and clear inline overrides.
      tilesEl.classList.add('is-collapsed');
      tilesEl.style.maxHeight = '';
      tilesEl.style.overflow = '';
    });
    this.setExpandBtnState(expandBtn, false);
  }

  // The collapsed cap is whatever the `.is-collapsed { max-height }` rule for
  // this grid says — it is only readable while that class is applied and no
  // inline max-height is set, so cache it for the collapse direction.
  collapsedCap(tilesEl) {
    const measured = parseFloat(getComputedStyle(tilesEl).maxHeight);
    if (!Number.isNaN(measured)) {
      tilesEl.dataset.collapsedMax = String(measured);
      return measured;
    }
    return parseFloat(tilesEl.dataset.collapsedMax) || MegaDrawer.COLLAPSED_MAX;
  }

  // Animate max-height to a target value via rAF, running `onDone` when the
  // max-height transition settles (with a timeout fallback).
  animateTilesTo(tilesEl, targetMaxHeight, onDone) {
    this.tilesAnimating = true;
    let finished = false;
    const done = (event) => {
      if (event && event.propertyName !== 'max-height') return;
      if (finished) return;
      finished = true;
      tilesEl.removeEventListener('transitionend', done);
      clearTimeout(fallback);
      this.tilesAnimating = false;
      onDone();
    };
    tilesEl.addEventListener('transitionend', done);
    // Fallback in case transitionend never fires (e.g. no height change).
    const fallback = setTimeout(done, MegaDrawer.TILES_DURATION + 60);
    requestAnimationFrame(() => {
      tilesEl.style.maxHeight = targetMaxHeight;
    });
  }

  // Swap the button label with a quick text-shrink animation and keep
  // aria-expanded / the caret direction in sync.
  setExpandBtnState(expandBtn, expanded, animate = true) {
    expandBtn.setAttribute('aria-expanded', String(expanded));
    expandBtn.classList.toggle('is-expanded', expanded);

    const labelEl = expandBtn.querySelector('.mega-drawer__expand-label') || expandBtn;
    const nextText = expanded
      ? expandBtn.dataset.collapseLabel
      : expandBtn.dataset.expandLabel;
    if (nextText == null || nextText === labelEl.textContent) return;

    if (!animate || this.prefersReducedMotion() || typeof labelEl.animate !== 'function') {
      labelEl.textContent = nextText;
      return;
    }

    labelEl
      .animate(
        [
          { transform: 'scale(1)', opacity: 1 },
          { transform: 'scale(0.6)', opacity: 0 },
        ],
        { duration: 110, easing: 'ease-in', fill: 'forwards' }
      )
      .finished.then(() => {
        labelEl.textContent = nextText;
        labelEl.animate(
          [
            { transform: 'scale(0.6)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: 160, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'forwards' }
        );
      })
      .catch(() => {
        // Animation was cancelled — make sure the label still updates.
        labelEl.textContent = nextText;
      });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  getActiveLevel() {
    return this.querySelector('.mega-drawer__level.is-active');
  }

  scrollTopOfActive() {
    const level = this.getActiveLevel();
    if (level && typeof level.scrollTo === 'function') level.scrollTo({ top: 0 });
  }

  focusFirstFocusable(container) {
    if (!container) return;
    const focusable = container.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus({ preventScroll: true });
  }
}

if (!customElements.get('mega-drawer')) {
  customElements.define('mega-drawer', MegaDrawer);
}
